import { Action, combineReducers, createFeatureSelector, createSelector } from '@ngrx/store';
import { getFilterDate, getProcessDateInIsoFormat } from '../../create-request/components/transactions-table/table-helpers';
import { CashieringTransaction } from '../../create-request/models';
import { selectCheckInfo, selectCheckUseStandingAuth, selectInternalTransferInfo, selectJournalsUseStandingAuth, selectTransactionType, selectUseStandingAuthVal } from '../../create-request/reducers';
import { selectCreateTypeInContext, selectEAuthInfo } from '../../shared/reducers';
import { InstructionStatusCdDict, accountRestrictionReasons, firstTimeUserAgreementText, transactionDirection, transactionTypes } from '../../shared/utilities/Constants';
import { CashBalance } from '../models';
import * as fromAccountInfo from './account-info.reducers';
import * as fromCashBalances from './cash-balances.reducers';
import * as fromHeader from './header-state.reducer';
import * as fromSubmitCancelTransactions from './submit-cancel-transaction.reducer';
import * as fromSubmitDeleteStandingAuth from './submit-delete-standing-auth.reducer';
import * as fromEditTweModalStateReducer from './update-twe-modal-state.reducers';
import * as fromTaxes from './tax-info.reducer';
import * as fromTemplates from './templates.reducer';
import * as fromTransactions from './transactions.reducers';
import { TaxWithholdingFields } from '../../create-request/models/form/TransactionForm';

export const accountInfoFeatureKey = 'account';

export interface AccountInfoState {
    [fromCashBalances.cashFeatureKey]: fromCashBalances.State;
    [fromAccountInfo.infoKey]: fromAccountInfo.State;
    [fromTaxes.taxesKey]: fromTaxes.State;
    [fromHeader.headerStateKey]: fromHeader.State;
    [fromTransactions.transactionsKey]: fromTransactions.State;
    [fromSubmitCancelTransactions.submitCancelFeatureKey]: fromSubmitCancelTransactions.State;
    [fromTemplates.templatesKey]: fromTemplates.State;
    [fromSubmitDeleteStandingAuth.submitDeleteFeatureKey]: fromSubmitDeleteStandingAuth.State;
    [fromEditTweModalStateReducer.editTweModalStateKey]: fromEditTweModalStateReducer.State;
}

export function reducers(state: AccountInfoState | undefined, action: Action) {
    return combineReducers({
        [fromCashBalances.cashFeatureKey]: fromCashBalances.cashReducer,
        [fromAccountInfo.infoKey]: fromAccountInfo.accountInfoReducer,
        [fromTaxes.taxesKey]: fromTaxes.reducer,
        [fromHeader.headerStateKey]: fromHeader.reducer,
        [fromTransactions.transactionsKey]: fromTransactions.transactionsReducer,
        [fromSubmitCancelTransactions.submitCancelFeatureKey]: fromSubmitCancelTransactions.submitCancelTransactionReducer,
        [fromTemplates.templatesKey]: fromTemplates.templatesReducer,
        [fromSubmitDeleteStandingAuth.submitDeleteFeatureKey]: fromSubmitDeleteStandingAuth.submitDeleteStandingAuthReducer,
        [fromEditTweModalStateReducer.editTweModalStateKey]: fromEditTweModalStateReducer.reducer
    })(state, action);
}

export const selectAccountInfo = createFeatureSelector<AccountInfoState>(
    accountInfoFeatureKey
);

export const selectCashBalances = createSelector(
    selectAccountInfo,
    (state) => state.cashBalances
);

export const selectInfo = createSelector(selectAccountInfo, (state) => {
    return state.info;
});

export const selectClassifications = createSelector(selectInfo, (state) => {
    return state.classifications;
});

export const selectEAuthorization = createSelector(selectInfo, (state) => {
    return state.eauthorization;
})

export const selectUsername = createSelector(selectInfo, (state) => {
    return state.userName;
})

export const selectOverviewFailed = createSelector(selectInfo, (state) => {
    return state.overviewFailed;
});

export const selectAccountInfoSuccessfullyCompleted = createSelector(selectInfo, (state) => {
    return state.accountInfoCallSuccess;
});

export const selectIsIra = createSelector(selectClassifications, (state) => {
    return state.isIra;
})

export const selectTransactions = createSelector(selectAccountInfo, (state) => {
    return state.transactions;
});

export const selectTemplates = createSelector(selectAccountInfo, (state) => {
    return state.templates;
})

export const selectTemplatesList = createSelector(selectTemplates, (state) => {
    return state.templatesList;
})

export const selectTemplatesListForm = createSelector(selectTemplates, (state) => {
    return state.templateListForm;
})

export const selectTemplatesDeleteCallFinished = createSelector(selectTemplates, (state) => {
    return state.templatesDeleteCallFinished;
})

export const selectTemplatesListCallTableSucceeded = createSelector(selectTemplates, (state) => {
    return state.templatesListCallTableSucceeded;
})

export const selectTemplatePrefilledFlowStarted = createSelector(selectTemplates, (state) => state.templatesPrefilledFlowInProgress);

export const selectTemplateStartTransactionStarted = createSelector(
  selectTemplates,
  (state) => state.templateStartTransactionFlowInProgress
);

export const selectHasNoSavedTemplates = createSelector(selectTemplatesList, (state) => {
    return state?.length <= 0;
})

export const selectTemplatesListEmpty = createSelector(selectTemplatesList, (state) => {
    return state.length === 0;
})

export const selectCashieringTransactions = createSelector(
    selectTransactions,
    (state) => {
        return state.cashieringTransactions;
    }
);

export const selectIsPendingCheckSloaSetup = createSelector(
    selectCheckInfo,
    selectCashieringTransactions,
    (checkInfo, cashieringTransactions) => {
        const trans: CashieringTransaction | undefined = cashieringTransactions.find(trans => trans.cashieringRqstId === checkInfo.cashieringRequestId.toString())
        if (trans) {
            return trans.mediaTypeCd === 'C' && trans.isSloaSetup && trans.statusCd === 'PEND';
        }
        return false;
    }
);

export const selectIsPendingItSloaSetup = createSelector(
    selectInternalTransferInfo,
    selectCashieringTransactions,
    (itInfo, cashieringTransactions) => {
        const trans: CashieringTransaction | undefined = cashieringTransactions.find(trans => trans.cashieringRqstId === itInfo.cashieringReqId.toString())
        if (trans) {
            return trans.mediaTypeCd === 'J' && trans.isSloaSetup && trans.statusCd === 'PEND';
        }
        return false;
    }
);

export const selectCashieringRecurringTransactions = createSelector(
    selectTransactions,
    (state) => {
        return state.cashieringRecurringTransactions;
    }
);

export const selectAchRecurringProfiles = createSelector(
    selectCashieringRecurringTransactions,
    (recurringProfiles) => {
        return recurringProfiles.filter(profile => profile.mediaTypeCd === 'A')
    }
)

export const selectUpcomingTransactions = createSelector(
    selectCashieringTransactions,
    selectCashieringRecurringTransactions,
    (stateCashiering, stateRecurring) => {
        let stateCash = stateCashiering.filter((val) => {
            const today = getFilterDate();
            let count = 0;
            let processDate: Date;
            if (val.processDate?.includes('As of')) {
                // In the case where an 'As of' date is given, grab the first date in the string which represents the actual process date
                processDate = new Date(getProcessDateInIsoFormat(val.processDate.split(' ')[0]));
            } else {
                processDate = new Date(getProcessDateInIsoFormat(val.processDate));
            }
            const resolvedStatus =
                val.statusCodeDescription === 'Cancelled'
                || val.statusCodeDescription === 'Inactive'
                || val.statusCodeDescription === 'Void'
                || val.statusCodeDescription === 'Complete'
                || val.statusCodeDescription === 'Failed';
            if (
                count < 500 &&
                (processDate >= today.onlyDate &&
                    !resolvedStatus) ||
                (val.processDate === 'N/A' &&
                    (val.isRecurring ||
                        val.statusCodeDescription === 'Pending' ||
                        val.statusCodeDescription === 'Pending-Review'))
            ) {
              count++;
              return true;
            } else {
              return false;
            }
        });
        let stateRec = stateRecurring.filter((val) => {
            const next15Day = getFilterDate(14);
            const today = getFilterDate();
            const processDate = new Date(val.processDate);
            return (
                processDate >= today.onlyDate &&
                processDate <= next15Day.onlyDate &&
                (val.statusCodeDescription === 'Pending' || val.statusCodeDescription === 'Active' || val.statusCodeDescription === 'Approved')
            );
        });
        return [...stateCash, ...stateRec];
    }
);

export const selectRecentTransactions = createSelector(
    selectCashieringTransactions,
    (state) => {
        return state.filter(
            (val) => {
                let processDate: Date;
                let count = 0;
                if (val.processDate?.includes('As of')) {
                    // In the case where an 'As of' date is given, grab the first date in the string which represents the actual process date
                    processDate = new Date(getProcessDateInIsoFormat(val.processDate.split(' ')[0]));
                } else {
                    processDate = new Date(getProcessDateInIsoFormat(val.processDate));
                }
                const today = getFilterDate();
                const last90Day = getFilterDate(-90);
                const resolvedStatus =
                    val.statusCodeDescription === 'Cancelled'
                    || val.statusCodeDescription === 'Inactive'
                    || val.statusCodeDescription === 'Void'
                    || val.statusCodeDescription === 'Complete'
                    || val.statusCodeDescription === 'Failed';
                if (
                  (count < 500 &&
                    processDate < today.onlyDate &&
                    processDate >= last90Day.onlyDate) ||
                  resolvedStatus
                ) {
                  count++;
                  return true;
                } else {
                  return false;
                }
            }
        );
    }
);

export const selectAllTransactions = createSelector(
    selectRecentTransactions,
    selectUpcomingTransactions,
    (recentTransactions, upcomingTransactions) => {
        return [...recentTransactions, ...upcomingTransactions];
    }
);

export const selectRecurringTransactions = createSelector(
    selectCashieringRecurringTransactions,
    (state) => {
        return state.filter(
            (val) => {
                const processDate = new Date(getProcessDateInIsoFormat(val.processDate))
                return processDate;
            }
        );
    }
);

export const selectHasNoTransactions = createSelector(
    selectUpcomingTransactions,
    selectRecentTransactions,
    (recent, upcoming) => {
    return recent?.length <= 0 && upcoming?.length <= 0;
});

export const selectHasNoUpcomingTransactions = createSelector(selectUpcomingTransactions, (state) => {
    return state?.length <= 0;
});

export const selectHasNoRecentTransactions = createSelector(selectRecentTransactions, (state) => {
    return state?.length <= 0;
});

export const selectHasNoRecurringTransactions = createSelector(selectRecurringTransactions, (state) => {
    return state?.length <= 0;
})

export const selectCashByAccountType = createSelector(
    selectCashBalances,
    selectInfo,
    (balanceState, AccountInfoState) => {
        if (AccountInfoState.classifications.isPtrs) {
            return [
                balanceState.availableCash,
                balanceState.unsettledCash,
                balanceState.trustIncomeBalance,
                balanceState.trustPrincipalBalance,
                balanceState.marginAvailable,
                balanceState.marketValue,
            ];
        }
        else if ((AccountInfoState.registrationType.toLowerCase()).includes('inh')) {
            return [
                balanceState.availableCash,
                balanceState.unsettledCash,
                balanceState.marketValue
            ];
        }
        else if (AccountInfoState.classifications.isIra) {
            if((AccountInfoState.registrationType.includes('Part QRP/Keo')) || (AccountInfoState.registrationType.includes('I401(k)'))){
                return AccountInfoState.classifications.isRmdEligible ?
                [
                    balanceState.availableCash,
                    balanceState.unsettledCash,
                    balanceState.marketValue,
                    balanceState.rmdCurrentYear,
                    balanceState.rmdYtd,
                    balanceState.rmdRemaining,
                ] :
                [
                    balanceState.availableCash,
                    balanceState.unsettledCash,
                    balanceState.marginAvailable,
                    balanceState.marketValue
                ];
            }
            else {
                return AccountInfoState.classifications.isRmdEligible ?
                [
                    balanceState.availableCash,
                    balanceState.unsettledCash,
                    balanceState.marketValue,
                    balanceState.contributionYtd,
                    balanceState.rmdCurrentYear,
                    balanceState.rmdYtd,
                    balanceState.rmdRemaining,
                ] :
                [
                    balanceState.availableCash,
                    balanceState.unsettledCash,
                    balanceState.marginAvailable,
                    balanceState.marketValue,
                    balanceState.contributionYtd
                ];
            }
        }
        else {
            return [
                balanceState.availableCash,
                balanceState.unsettledCash,
                balanceState.marginAvailable,
                balanceState.marketValue,
            ];
        }
    }
);

export const selectPalBalances = createSelector(
    selectCashBalances,
    (balanceState) => {
        return [
            balanceState.currentBalance,
            balanceState.availableCredit,
            balanceState.displayCredit,
            balanceState.billingTotalChargesDue,
            balanceState.billingTotalPaymentDue,
            balanceState.billingPaymentDueDt
        ].filter((balance): balance is CashBalance => balance !== undefined);
    }
);

export const noTransactionSelected = createSelector(
    selectTransactionType,
    (state) => {
        return (state === '');
    }
)

export const selectTaxInfo = createSelector(selectAccountInfo, (state) => {
    return state.taxes;
});

export const selectStateTaxRate = createSelector(selectAccountInfo, (state) => {
    return state.taxes.stateTaxRate;
});

export const selectStateCode = createSelector(selectAccountInfo, (state) => {
    return state.taxes.state;
});

export const selectHeader = createSelector(selectAccountInfo, (state) => {
    return state.headerState;
});

export const selectHeaderStateServiceError = createSelector(
    selectHeader,
    (state) => {
        return state.serviceError;
    }
);

export const selectAgreement = createSelector(selectTransactions, (state) => {
    return state.agreement;
});

export const selectShouldDisplayAgreement = createSelector(selectAgreement, (state) => {
    return state.hasAgreementResponseError || state.canDisplayAgreement;
});

export const selectAgreementText = createSelector(selectAgreement, (state) => {
    return state.agreementText.length > 0 ? state.agreementText : firstTimeUserAgreementText;
})

export const selectSubAccountId = createSelector(selectInfo, (state) => {
    return state.accountId;
});

export const selectUserContext = createSelector(selectInfo, (state) => state.userContext);

export const selectIsDbsUser = createSelector(selectInfo, (state) => {
    return state.classifications.isDbsUser;
});

export const selectIsEmployee = createSelector(selectInfo, (state) => {
    return state.classifications.isEmployee;
});

export const selectIsDemoUser = createSelector(selectInfo, (state) => {
    return state.classifications.isDemoUser;
});

export const selectIsFsaUser = createSelector(selectInfo, (state) => {
    return state.classifications.isFsaUser;
});

export const selectIsUserInputRequired = createSelector(selectEAuthInfo, (state) => {
  return state.userInputRequired;
});

export const selectEAuthCustomers = createSelector(selectInfo, (state) => {
    return state.eauthorization.customers;
});

export const selectRestrictionReasons = createSelector(selectEAuthorization, (state) => {
  return state.restrictionReason;
});

export const selectAreAccountAssetsPledged = createSelector(selectRestrictionReasons, (state) => {
  return state?.includes(accountRestrictionReasons.assetsPledged) ?? false;
});

export const selectIsOrgAccount = createSelector(selectInfo, (state) => {
    return state.eauthorization.isOrgAccount
});

export const selectInstructions = createSelector(
    selectTransactions,
    (state) => {
        const instructions = state.standingInstructions;
        const ids = Object.keys(instructions);

        const instructionsArray = ids
            .map((id) => instructions[`${id}`]);
        return instructionsArray;
    }
);


export const selectHasNoStandingInstructions = createSelector(
    selectInstructions,
    selectAchRecurringProfiles,
    (instructions, achProfiles) => {
        return instructions?.length <= 0 && achProfiles?.length <= 0;
    });

export const selectItStandingAuth = createSelector(selectInstructions, (state) => {
    return state.filter(instruction => instruction.transactionType === transactionTypes.internalTransfer);
});

export const selectHasNoItStandingInstructions = createSelector(selectItStandingAuth, (state) => {
    return state?.length <= 0;
});

export const selectFilteredItStandingAuth = createSelector(selectItStandingAuth, selectJournalsUseStandingAuth, (state, useStandingAuth) => {
    return state.filter((instruction) => {
      return useStandingAuth === 'bundled-yes'
        ? instruction.isRequestTransaction &&
            instruction.isBundled &&
            instruction.statusCd === InstructionStatusCdDict.active
        : instruction.isRequestTransaction && !instruction.isBundled;
    });
});

export const selectChecksStandingAuth = createSelector(selectInstructions, (state) => {
    return state.filter(instruction => instruction.transactionType === transactionTypes.check);
});

export const selectHasNoCheckStandingInstructions = createSelector(selectChecksStandingAuth, (state) => {
    return state?.length <= 0;
});

export const selectFilteredChecksStandingAuth = createSelector(
  selectChecksStandingAuth,
  selectCheckUseStandingAuth,
  (instructions, useStandingAuthVal) => {
    if (useStandingAuthVal === 'bundled-yes') {
      return instructions.filter(
        (instruction) =>
          instruction.statusCd === InstructionStatusCdDict.active &&
          instruction.isRequestTransaction &&
          instruction.isBundled
      );
    } else {
      return instructions.filter(
        (instruction) =>
          instruction.isRequestTransaction && !instruction.isBundled
      );
    }
  }
);

export const selectWiresStandingAuth = createSelector(selectInstructions, (state) => {
    return state.filter(instruction => instruction.transactionType === transactionTypes.wire);
});

export const selectHasNoWireStandingInstructions = createSelector(selectWiresStandingAuth, (state) => {
    return state?.length <= 0;
});

export const selectFilteredWiresStandingAuth = createSelector(selectWiresStandingAuth, (state) => {
    return state.filter(instruction => instruction.isRequestTransaction === true);
});

export const selectAchInstructions = createSelector(selectInstructions, (instructions) => {
    return instructions.filter(instruction => instruction.transactionType === transactionTypes.ach);
});

export const selectHasNoAchStandingInstructions = createSelector(
    selectAchInstructions,
    selectAchRecurringProfiles,
    (instructions, achProfiles) => {
        return instructions?.length <= 0 && achProfiles?.length <= 0;
    });

export const selectFilteredAchStandingAuth = createSelector(selectAchInstructions, (state) => {
    return state.filter(instruction => instruction.isRequestTransaction === true);
});

export const selectActiveWireInstructions = createSelector(
  selectWiresStandingAuth,
  selectUseStandingAuthVal,
  (instructions, useStandingAuthVal) => {
    if (useStandingAuthVal === 'bundled-yes') {
      return instructions.filter(
        (instruction) =>
          instruction.statusCd === InstructionStatusCdDict.active &&
          instruction.isRequestTransaction &&
          instruction.isBundled
      );
    } else {
      return instructions.filter(
        (instruction) =>
          instruction.statusCd === InstructionStatusCdDict.active &&
          instruction.isRequestTransaction &&
          !instruction.isBundled
      );
    }
  }
);

export const selectActiveOnRequestAchInstructions = createSelector(selectAchInstructions, (instructions) => {
    return instructions.filter(instruction => instruction.statusCd === InstructionStatusCdDict.active && instruction.isRequestTransaction && instruction.frequencyDescription === null);
})

export const selectActiveOnRequestAndRecurringAchInstructions = createSelector(selectAchInstructions, (instructions) => {
    return instructions.filter(instruction => instruction.statusCd === InstructionStatusCdDict.active && instruction.isRequestTransaction);
})

export const selectActiveAchIncomingInstructions = createSelector(selectActiveOnRequestAchInstructions, (instructions) => {
    return instructions.filter(instruction => instruction.fundsDirection === transactionDirection.in);
})

export const selectActiveRecurringAchInstructions = createSelector(selectAchInstructions, (instructions) => {
    return instructions.filter(instruction => instruction.statusCd === InstructionStatusCdDict.active && instruction.frequencyDescription != null);
})

export const selectActiveAchInstructions = createSelector(selectAchInstructions, (instructions) => {
    return instructions.filter(instruction => instruction.statusCd === InstructionStatusCdDict.active);
})

export const selectLoadPopupCallFinished = createSelector(selectTransactions, (state) => {
    return state.loadPopupCallFinished;
})

export const selectLoadTransactionsCallFinished = createSelector(selectTransactions, (state) => {
    return state.loadTransactionsSuccess;
})

export const selectMoveMoneyDataAccess = createSelector(selectTransactions, (state) => {
    return state.hasMoveMoneyDataAccess;
});

export const selectMasterAccount = createSelector(selectInfo, (state) => {
    return state.masterAccount;
});

export const selectTransactionAuthorizationDetails = createSelector(selectTransactions, (state) => {
    return state.transactionAuthorizationDetails;
});

export const selectIsValidRegType = createSelector(selectTransactions, (state) => {
    return state.isValidRegType;
});

export const selectNraIndicator = createSelector(selectAccountInfo, (state) => {
    return state.info.classifications.isNraAccount;
});

export const selectIsPtrs = createSelector(selectAccountInfo, (state) => {
    return state.info.classifications.isPtrs;
})

export const selectRegType = createSelector(selectAccountInfo, (state) => {
    return state?.info.registrationType;
})

export const selectHasBundled = createSelector(selectAccountInfo, (state) => {
    return state.info.classifications.hasBundledOffer;
})

export const selectIsIip = createSelector(selectAccountInfo, (state) => {
    return state.info.classifications.isIip
})

export const selectIsTestamentaryTrust = createSelector(selectAccountInfo, (state) => {
    return state.info.classifications.isTestamentaryTrust;
})

export const selectIsGuardianship = createSelector(selectAccountInfo, (state) => {
    return state.info.classifications.isGuardian;
})

export const selectIsConservatorship = createSelector(selectAccountInfo, (state) => {
    return state.info.classifications.isConserv;
})

export const selectIsEstate = createSelector(selectRegType, (regType) => {
    return regType === 'Estate';
})

export const selectUseTestTrustDigitalToPdfFlow = createSelector(
    selectIsTestamentaryTrust,
    (isTestamentaryTrust) => {
        return isTestamentaryTrust;
    }
)

export const selectIsGuardianshipOrConservatorship = createSelector(
    selectIsGuardianship,
    selectIsConservatorship,
    (isGuardianship, isConservatorship) => {
    return isGuardianship || isConservatorship;
})

export const selectIsEstateOrTestTrust = createSelector(
    selectIsTestamentaryTrust,
    selectIsEstate,
    (isTestTrust, isEstate) => {
    return isTestTrust || isEstate;
})

export const selectHasGoingDigitalFlowWireCreateSloa = createSelector(
    selectIsEstateOrTestTrust,
    (isEstateOrTestTrust) => {
        return isEstateOrTestTrust;
    }
)

export const selectHasGoingDigitalFlowCheckCreateSloa = createSelector(
    selectIsEstateOrTestTrust,
    (isEstateOrTestTrust) => {
        return isEstateOrTestTrust;
    }
)

export const selectHasGoingDigitalFlowInternalTransferCreateSloa = createSelector(
    selectIsEstateOrTestTrust,
    (isEstateOrTestTrust) => {
        return isEstateOrTestTrust;
    }
)

export const selectAcknowledgementText = createSelector(selectInfo, (state) => {
    return state.acknowledgementText
})

export const selectRegistrationType = createSelector(selectInfo, (state) => {
    return state.registrationType;
})

export const selectIsJointAccount = createSelector(selectRegistrationType, (state) => state === 'Jt Ten');

export const selectIsPensionTrustAccount = createSelector(selectRegistrationType, (state) => state === 'Pen Trust');

export const selectSubmitCancelTransactionState = createSelector(
    selectAccountInfo,
    (state) => {
        return state.submitCancel
    })

export const selectcancelledTransactionId = createSelector(
    selectSubmitCancelTransactionState,
    (state) => {
        return state.cancelledTransactionId;
    }
)

export const selectSubmitCancelTransaction = createSelector(
    selectSubmitCancelTransactionState,
    (state) => {
        return state.isCancelTransactionSubmitted
    })

export const selectHasCreateStandingAuthAccess = createSelector(selectInfo, (state) => {
    return state.hasStandingAuthorizationAccess;
})

export const selectSubmitDeleteStandingAuthState = createSelector(
  selectAccountInfo,
  (state) => {
      return state.submitDelete;
})

export const selectSubmitDeactivateTransactionState = createSelector(
  selectAccountInfo,
  (state) => {
      return state.submitCancel
})

export const selectCreateWireTemplateCallCompleted = createSelector(
    selectTemplates,
    (state) => {
        return state.createWireTemplateCallCompleted;
})

export const selectCreateWireTemplateCallSuccessful = createSelector(
    selectTemplates,
    (state) => {
        return state.createWireTemplateCallSuccessful;
})

export const selectPatchWireTemplateCallCompleted = createSelector(
    selectTemplates,
    (state) => {
        return state.patchWireTemplateCallCompleted;
})

export const selectTemplateNickNameCallSuccessful = createSelector(
    selectTemplates,
    (state) => {
        return state.templatesAddUpdateEditNickNameCallSuccessful;
})

export const selectTemplateDeleteCallSuccessful = createSelector(
    selectTemplates,
    (state) => {
        return state.templateDeleteCallSuccessful;
    }
)

export const selectIsLivingTrust = createSelector(
    selectInfo,
    (state) => {
        return state.registrationType?.toLowerCase() === "liv trust";
    }
)

export const selectEditTweModalState = createSelector(
    selectAccountInfo,
    (state) => {
        return state.editTweModalState;
    }
)

export const selectOpenTwePopup = createSelector(
    selectEditTweModalState,
    (state) => {
        return state.twePopup.openTwePopup;
    }
)

export const selectEditTweVerifyCallFinished = createSelector(
    selectEditTweModalState,
    (state) => {
        return state.verifyCallFinished;
    }
)

export const selectEditTweServiceError = createSelector(
    selectEditTweModalState,
    (state) => {
        return state.serviceError;
    }
)

export const selectTaxWithholdings = createSelector(
    selectEditTweModalState,
    (state) => {
      return {
        withholdFedTax: false,
        withholdStateTax: false,
        fedTaxPercent: state.twePopup.fedTaxRateUserEntered.toString() + '%',
        fedTaxAmount: '',
        stateTaxPercent: state.twePopup.stateTaxRateUserEntered.toString() + '%',
        stateTaxAmount: '',
        distributionReason: '',
        distributionReasonMap: [],
        fedTaxRate: state.twePopup.fedTaxRateUserEntered,
        stateTaxRate: state.twePopup.stateTaxRateUserEntered
      } as TaxWithholdingFields
    }
)

export const selectTweCashieringTransactions = createSelector(
    selectEditTweModalState,
    (state) => {
        return state.twePopup?.accountDetails?.cashieringRequestAprs;
    }
)

export const selectTweStandingAuth = createSelector(
    selectEditTweModalState,
    (state) => {
        return state.twePopup?.accountDetails?.sloaRequests;
    }
)

export const selectTweRecurringTransactions = createSelector(
    selectEditTweModalState,
    (state) => {
        return state.twePopup?.accountDetails?.recurringRequests;
    }
)

export const selectEditTweSubmitCallFinished = createSelector(
    selectEditTweModalState,
    (state) => {
        return state.submitCallFinished;
    }
)

export const selectTweUpcomingTransactions = createSelector(
    selectTweCashieringTransactions,
    selectTweRecurringTransactions,
    (cashieringTransactions, recurringTransactions) => {
        let transactions = cashieringTransactions.filter((val) => {
            let count = 0;
            const today = getFilterDate()
            const processDate = new Date(val.formattedDate)
            const resolvedStatus =
              val.cashrRequestStatCd === 'CANC' ||
              val.cashrRequestStatCd === 'INAC' ||
              val.cashrRequestStatCd === 'VOID' ||
              val.cashrRequestStatCd === 'COMP' ||
              val.cashrRequestStatCd === 'STOP' ||
              val.cashrRequestStatCd === 'FAIL';
            if (count < 500 &&
              (processDate >= today.onlyDate && !resolvedStatus && !val.typeDescription.includes('SLOA')) ||
              (val.typeDescription.includes('Recurring') && val.cashrRequestStatCd === 'PEND'))
            {
              count++;
              return true;
            } else {
              return false;
            }
          });
      let recTransactions = recurringTransactions.filter((val) => {
        const next15Day = getFilterDate(14)
        const today = getFilterDate()
        const processDate = new Date(val.formattedDate)
        const activeStatus =
            val.recurStatusCode === 'ACTV' ||
            val.recurStatusCode === 'PEND' ||
            val.recurStatusCode === 'APPR';
        return (
          processDate >= today.onlyDate &&
          processDate <= next15Day.onlyDate &&
          activeStatus
        );
      });
      return [...transactions, ...recTransactions]
});

export const selectIsBtMasterOnly = createSelector(
    selectInfo, (state) => {
        return state.isBtMasterOnly;
    }
)

export const selectPrimaryMaster = createSelector(
    selectInfo, (state) => {
        return state.primaryMaster;
    }
)
